import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import type { CSSProperties } from 'react';
import { useMemo, useState } from 'react';
import { useFiltersByName } from 'src/hooks/useFiltersByName';
import { IObject } from 'src/models/objectManagement';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import SearchIcon from '@mui/icons-material/Search';
import { FixedSizeList as List } from 'react-window';
import GetSocialMediaIconWithBgColor from 'src/components/GetSocialMediaIconWithBgColor';
import { ITabList } from '..';
import Empty from 'src/components/Empty';
import { useAppSelector } from 'src/app/hooks';
import ModalAddSocialProfileRetail from '../../ModalAddSocialProfileRetail';
import ModalAddKeywordAndHashtagRetail from '../../ModalAddKeywordAndHashtagRetail';
import ModalAddSocialProfile from 'src/components/ModalAddSocialProfile';
import ModalAddKeywordAndHashtag from 'src/components/ModalAddKeywordAndHashtag';
import ModalAddPostLink from 'src/components/ModalAddPostLink';
import { TSocialMedia } from 'src/models/general';
import FilterObjectByPlatform, {
  SOCIAL_MEDIA_LIST
} from 'src/components/FilterObjectByPlatform';

export type ITabValue = 'all' | 'account' | 'keyword' | 'hashtag' | 'post_link';

const OBJECT_ROW_HEIGHT = 41;

export default function SelectObjectSection({
  objectList,
  activeObjectIdList,
  selectedTab,
  setSelectedTab,
  tabList,
  onSelectObject,
  isGoogleReview,
  isEditing,
  isStreamPanel,
  isCustomDashboard,
  handleDone,
  disableSubmit
}: {
  objectList: Array<IObject>;
  activeObjectIdList: Array<string>;
  tabList: ITabList[];
  selectedTab: ITabValue;
  setSelectedTab: React.Dispatch<React.SetStateAction<ITabValue>>;
  onSelectObject: (objectIds: string[]) => void;
  isGoogleReview: boolean;
  isEditing: boolean;
  isStreamPanel: boolean;
  isCustomDashboard: boolean;
  handleDone?: () => void;
  disableSubmit?: boolean;
}) {
  const theme = useTheme();

  const { groupSelectedById } = useAppSelector(
    (state) => state.objectManagement
  );
  const {
    customerType,
    flags: { isGoogleReview: isGoogleReviewActive }
  } = useAppSelector((state) => state.users.project);

  const showGoogleReview =
    Boolean(isGoogleReviewActive) &&
    (isEditing || isStreamPanel || isCustomDashboard);

  const [selectedPlatforms, setSelectedPlatforms] = useState<TSocialMedia[]>(
    showGoogleReview
      ? [...SOCIAL_MEDIA_LIST, 'googleReview']
      : SOCIAL_MEDIA_LIST
  );

  const isRetail = customerType === 'b2c';

  const handlePlatformChange = (platform: TSocialMedia) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform]
    );
  };

  const handleClickAllPlatform = () => {
    setSelectedPlatforms(
      showGoogleReview
        ? [...SOCIAL_MEDIA_LIST, 'googleReview']
        : SOCIAL_MEDIA_LIST
    );
  };

  const filteredObjectListByTab = useMemo(() => {
    if (selectedTab === 'all') return objectList;
    if (selectedTab === 'account')
      return isGoogleReview
        ? objectList.filter(
            (item) =>
              item.streamType === 'account' &&
              item.socialMedia === 'googleReview'
          )
        : objectList.filter((item) => item.streamType === 'account');
    if (selectedTab === 'keyword')
      return objectList.filter(
        (item) => item.streamType === 'keyword' && !item.isHashtag
      );
    if (selectedTab === 'hashtag')
      return objectList.filter(
        (item) => item.streamType === 'keyword' && item.isHashtag
      );
    if (selectedTab === 'post_link')
      return objectList.filter((item) => item.streamType === 'post_link');
  }, [selectedTab, objectList, isGoogleReview]);

  const objectListWithName = useMemo((): Array<IObject & { name: string }> => {
    return filteredObjectListByTab.map((object) => ({
      ...object,
      name: object.objectName
    }));
  }, [filteredObjectListByTab]);

  const { filteredList: filteredListByName, handleFilter } =
    useFiltersByName<IObject>(objectListWithName);

  const sortedListBySelected = useMemo(() => {
    if (!activeObjectIdList.length) return filteredListByName;
    if (!filteredListByName.length) return [];

    const activeIdsSet = new Set(activeObjectIdList);
    return filteredListByName.sort((a, b) => {
      const aIsActive = activeIdsSet.has(a.id);
      const bIsActive = activeIdsSet.has(b.id);
      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;
      return 0;
    });
  }, [filteredListByName, activeObjectIdList]);

  const filteredListByPlatform = useMemo(() => {
    return sortedListBySelected.filter((object) => {
      return selectedPlatforms.includes(object.socialMedia);
    });
  }, [sortedListBySelected, selectedPlatforms]);

  const { isCheckedAll, isIndeterminate } = useMemo(() => {
    const selectedIdsSet = new Set(activeObjectIdList);
    let checkedItemsCount = 0;
    if (selectedTab === 'account') {
      checkedItemsCount = isGoogleReview
        ? filteredListByPlatform.filter(
            (el) =>
              el.streamType === 'account' &&
              selectedIdsSet.has(el.id) &&
              isGoogleReview
          ).length
        : filteredListByPlatform.filter(
            (el) => el.streamType === 'account' && selectedIdsSet.has(el.id)
          ).length;
    } else if (selectedTab === 'keyword') {
      checkedItemsCount = filteredListByPlatform.filter(
        (el) =>
          el.streamType === 'keyword' &&
          !el.isHashtag &&
          selectedIdsSet.has(el.id)
      ).length;
    } else if (selectedTab === 'hashtag') {
      checkedItemsCount = filteredListByPlatform.filter(
        (el) =>
          el.streamType === 'keyword' &&
          el.isHashtag &&
          selectedIdsSet.has(el.id)
      ).length;
    } else if (selectedTab === 'post_link') {
      checkedItemsCount = filteredListByPlatform.filter(
        (el) => el.streamType === 'post_link' && selectedIdsSet.has(el.id)
      ).length;
    } else {
      checkedItemsCount = filteredListByPlatform.filter((el) =>
        selectedIdsSet.has(el.id)
      ).length;
    }
    const isCheckedAll = filteredListByPlatform.length === checkedItemsCount;
    const isIndeterminate =
      checkedItemsCount > 0 &&
      checkedItemsCount < filteredListByPlatform.length;

    return { isCheckedAll, isIndeterminate };
  }, [filteredListByPlatform, activeObjectIdList, selectedTab, isGoogleReview]);

  const handleSelectAll = () => {
    if (isCheckedAll) {
      onSelectObject(
        activeObjectIdList.filter(
          (item) =>
            !filteredListByPlatform.map((object) => object.id).includes(item)
        )
      );
    } else {
      onSelectObject([
        ...activeObjectIdList,
        ...filteredListByPlatform.map((object) => object.id)
      ]);
    }
  };

  const handleSelectObject = (objectId: string, isChecked: boolean) => {
    if (isChecked) {
      onSelectObject(activeObjectIdList.filter((id) => id !== objectId));
    } else {
      onSelectObject([...activeObjectIdList, objectId]);
    }
  };

  const totalSelectedObjects = {
    account: isGoogleReview
      ? objectList.filter(
          (item) =>
            item.streamType === 'account' &&
            activeObjectIdList.includes(item.id) &&
            item.socialMedia === 'googleReview'
        ).length
      : isEditing
      ? objectList.filter(
          (item) =>
            item.streamType === 'account' &&
            activeObjectIdList.includes(item.id)
        ).length
      : objectList.filter(
          (item) =>
            item.streamType === 'account' &&
            activeObjectIdList.includes(item.id) &&
            item.socialMedia !== 'googleReview'
        ).length,
    keyword: objectList.filter(
      (item) =>
        item.streamType === 'keyword' &&
        !item.isHashtag &&
        activeObjectIdList.includes(item.id)
    ).length,
    hashtag: objectList.filter(
      (item) =>
        item.streamType === 'keyword' &&
        item.isHashtag &&
        activeObjectIdList.includes(item.id)
    ).length,
    post_link: objectList.filter(
      (item) =>
        item.streamType === 'post_link' && activeObjectIdList.includes(item.id)
    ).length
  };

  const totalObjects = {
    account: isGoogleReview
      ? objectList.filter(
          (item) =>
            item.streamType === 'account' && item.socialMedia === 'googleReview'
        ).length
      : isEditing
      ? objectList.filter((item) => item.streamType === 'account').length
      : objectList.filter(
          (item) =>
            item.streamType === 'account' && item.socialMedia !== 'googleReview'
        ).length,
    keyword: objectList.filter(
      (item) => item.streamType === 'keyword' && !item.isHashtag
    ).length,
    hashtag: objectList.filter(
      (item) => item.streamType === 'keyword' && item.isHashtag
    ).length,
    post_link: objectList.filter((item) => item.streamType === 'post_link')
      .length
  };

  const ObjectRow = ({
    index,
    style
  }: {
    index: number;
    style: CSSProperties;
  }) => {
    const item = filteredListByPlatform[index];
    const isItemSelected = activeObjectIdList.includes(item.id);

    return (
      <Box
        style={style}
        sx={{
          p: 1,
          boxSizing: 'border-box',
          borderBottom:
            index !== filteredListByPlatform.length - 1
              ? '1px solid #dce0e5'
              : 0,
          borderStartStartRadius: index === 0 ? '16px' : 0,
          borderStartEndRadius: index === 0 ? '16px' : 0,
          borderEndStartRadius:
            index === filteredListByPlatform.length - 1 ? '16px' : 0,
          borderEndEndRadius:
            index === filteredListByPlatform.length - 1 ? '16px' : 0,
          cursor: 'pointer',
          backgroundColor: isItemSelected ? theme.colors.primary.light : 'white'
        }}
        onClick={() => handleSelectObject(item.id, isItemSelected)}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {isItemSelected ? (
            <CheckBoxIcon color="primary" />
          ) : (
            <CheckBoxOutlineBlankIcon color="primary" />
          )}
          <GetSocialMediaIconWithBgColor socialMedia={item.socialMedia} />
          <Typography fontSize="12px">
            {item.streamType === 'account'
              ? '@'
              : item.streamType === 'keyword' && !item.isHashtag
              ? '~'
              : ''}
            {item.objectName}
          </Typography>
        </Stack>
      </Box>
    );
  };

  return (
    <Stack spacing={1} sx={{ px: 4 }}>
      <Stack direction="row" justifyContent="end">
        {groupSelectedById === '_default' && selectedTab === 'account' ? (
          <ModalAddSocialProfileRetail
            buttonVariant="outlined"
            showButton={true}
            handleCloseModal={() => {}}
          />
        ) : null}
        {groupSelectedById === '_default' &&
        (selectedTab === 'keyword' || selectedTab === 'hashtag') ? (
          <ModalAddKeywordAndHashtagRetail
            streamType={selectedTab}
            buttonVariant="outlined"
            showButton={true}
            handleCloseModal={() => {}}
          />
        ) : null}
        {groupSelectedById === '_default' &&
        selectedTab === 'post_link' &&
        !isRetail ? (
          <ModalAddPostLink
            title="Post Link"
            buttonLabel="Post Link"
            showButton={true}
            handleCloseModal={() => {}}
          />
        ) : null}
      </Stack>
      <Stack height="380px" direction="row" sx={{ gap: 1 }} width="100%">
        <Stack
          height="380px"
          direction="column"
          justifyContent="space-between"
          width="20%"
        >
          <Stack
            sx={{ borderRadius: '16px', border: 1, borderColor: '#E0E0E0' }}
          >
            {tabList.map((item, index) => {
              return (
                <>
                  <Stack
                    key={item.value}
                    sx={{
                      py: 1,
                      px: 2,
                      borderStartStartRadius: index === 0 ? '16px' : 0,
                      borderStartEndRadius: index === 0 ? '16px' : 0,
                      borderEndStartRadius:
                        index === tabList.length - 1 ? '16px' : 0,
                      borderEndEndRadius:
                        index === tabList.length - 1 ? '16px' : 0,
                      cursor: 'pointer',
                      backgroundColor:
                        item.value === selectedTab
                          ? theme.colors.primary.light
                          : 'white'
                    }}
                    onClick={() => setSelectedTab(item.value)}
                  >
                    <Typography fontSize="14px">{item.label}</Typography>
                  </Stack>
                  {index !== tabList.length - 1 && <Divider />}
                </>
              );
            })}
          </Stack>
          <Stack spacing={2}>
            <Stack spacing={1}>
              <Typography fontSize="16px" fontWeight={600}>
                Objects selected
              </Typography>
              {tabList.some((item) => item.value === 'account') ? (
                <Typography fontSize="14px">
                  Account :{' '}
                  <b>
                    {totalSelectedObjects['account']} /{' '}
                    {totalObjects['account']}
                  </b>
                </Typography>
              ) : null}
              {tabList.some((item) => item.value === 'keyword') ? (
                <Typography fontSize="14px">
                  Keyword:{' '}
                  <b>
                    {totalSelectedObjects['keyword']} /{' '}
                    {totalObjects['keyword']}
                  </b>
                </Typography>
              ) : null}
              {tabList.some((item) => item.value === 'hashtag') ? (
                <Typography fontSize="14px">
                  Hashtag:{' '}
                  <b>
                    {totalSelectedObjects['hashtag']} /{' '}
                    {totalObjects['hashtag']}
                  </b>
                </Typography>
              ) : null}
              {tabList.some((item) => item.value === 'post_link') ? (
                <Typography fontSize="14px">
                  Post Link:{' '}
                  <b>
                    {totalSelectedObjects['post_link']} /{' '}
                    {totalObjects['post_link']}
                  </b>
                </Typography>
              ) : null}
            </Stack>
            <Stack width="100%" spacing={1} alignItems="center">
              <Box
                sx={{
                  border: 1,
                  borderColor: theme.colors.primary.main,
                  color: theme.colors.primary.main,
                  py: 1,
                  px: 4,
                  fontSize: '14px',
                  fontWeight: 600,
                  borderRadius: '16px',
                  cursor: 'pointer',
                  ':hover': {
                    opacity: 0.7
                  }
                }}
                onClick={() => onSelectObject([])}
              >
                Clear All
              </Box>
            </Stack>
          </Stack>
        </Stack>
        <Stack spacing={1} width="100%">
          <Stack
            sx={{
              py: 1,
              px: 2,
              borderRadius: '16px',
              border: 1,
              borderColor: '#E0E0E0'
            }}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography fontWeight={700}>All Objects</Typography>
            <FilterObjectByPlatform
              selectedPlatforms={selectedPlatforms}
              handleChange={handlePlatformChange}
              handleClickAll={handleClickAllPlatform}
              socialMediaList={
                showGoogleReview
                  ? [...SOCIAL_MEDIA_LIST, 'googleReview']
                  : SOCIAL_MEDIA_LIST
              }
            />
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                placeholder="Search Object"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                sx={{ width: '75%' }}
                onChange={(e) => handleFilter(e.target.value)}
              />
              <Box sx={{ minWidth: theme.spacing(15) }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon />}
                      checkedIcon={<CheckBoxIcon />}
                      indeterminateIcon={<IndeterminateCheckBoxIcon />}
                      indeterminate={isIndeterminate}
                    />
                  }
                  label="Select All"
                  onChange={handleSelectAll}
                  checked={isCheckedAll}
                />
              </Box>
            </Stack>
          </Stack>
          <Grid
            sx={{
              borderRadius: '16px',
              border: 1,
              borderColor: '#E0E0E0',
              overflow: 'auto',
              padding: 0,
              gap: 0
            }}
            container
            width="100%"
          >
            <Box width="100%">
              {filteredListByPlatform.length > 0 ? (
                <List
                  height={250}
                  itemCount={filteredListByPlatform.length}
                  itemKey={(index) => filteredListByPlatform[index].id}
                  itemSize={OBJECT_ROW_HEIGHT}
                  width="100%"
                >
                  {ObjectRow}
                </List>
              ) : (
                <Empty />
              )}
            </Box>
          </Grid>
        </Stack>
      </Stack>
      {handleDone && (
        <Stack direction="row" justifyContent="center" sx={{ py: 1 }}>
          <Button
            variant="contained"
            sx={{
              background: theme.palette.primary.main,
              color: theme.colors.primary.lighter
            }}
            size="medium"
            onClick={handleDone}
            disabled={disableSubmit ? disableSubmit : false}
          >
            Done
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
